import React from 'react';

import { useSubscription } from 'react-stomp-hooks';

import { useReactTableState } from 'hooks/useReactTableState';
import useUserHook from 'hooks/useUserHook';

import { useCopyVersionStore } from 'store/copyVersion.store';
import { useProjectTreeStore } from 'store/projectContract.store';

import { useQueryGetProjectVersions } from 'api/queries/getProjectVersions.query';
import { useQueryGetTagsPerContract } from 'api/queries/getTagsPerContract.query';

import NoData from 'components/common/NoData/noData';
import { TableV8 } from 'components/common/Tables/TableV8/tableV8';
import { TableContainer } from 'components/common/Tables/components/TableContainer/TableContainer';
import { IProgramVersion } from 'components/common/VersionCompareSelectorSection/VersionCompareSelectorWrapper/VersionCompareSelectorWrapper';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';

import { columnsV8 } from 'components/widgets/RenderLibraryTable/RenderLibraryTable.utils';
import { ProgramTags } from 'components/widgets/RenderLibraryTable/components/ProgramTags/programTags';
import { CopyVersionModal } from 'components/widgets/RenderLibraryTable/components/copyVersionModal/copyVersionModal';
import classes from 'components/widgets/RenderLibraryTable/libraryTable.module.scss';

const LibraryTable = () => {
    const { email } = useUserHook();
    const { project, contract } = useProjectTreeStore();
    const {
        data: versions,
        isLoading: isLoadingVersions,
        refetch: refetchVersion,
    } = useQueryGetProjectVersions({
        projectId: project?.id,
        contractId: contract?.id,
    });

    const { update } = useCopyVersionStore();
    const { data: tagsPerContract } = useQueryGetTagsPerContract({ contractId: contract?.id });

    useSubscription(email ? [`/topic/${email}.notifications.push`] : [], (response) => {
        const { type, message } = JSON.parse(response.body);
        if (type === 'VersionStatus') {
            if (message.metaDataId) {
                return refetchVersion();
            }
        }
    });

    const updatedVersions = versions
        ? versions.map((version) => {
              return {
                  ...version,
                  className:
                      version.status.toLowerCase() === 'pending'
                          ? classes.pendingStatus
                          : version.status.toLowerCase() === 'error'
                            ? classes.errorStatus
                            : '',
              };
          })
        : [];

    const { sorting, setSorting, columnFilters, setColumnFilters, rowSelection, setRowSelection } = useReactTableState({
        initialSorting: [
            {
                id: 'versionDate',
                desc: true,
            },
        ],
    });

    return (
        <>
            <WidgetWithTitle
                id={'uploaded_programs'}
                title={<WidgetTitle title={'Uploaded Programs'} postfix={`(${versions?.length})`} />}
                titleComponents={<ProgramTags list={tagsPerContract} />}
            >
                <TableContainer>
                    <TableV8<IProgramVersion>
                        columns={columnsV8}
                        data={updatedVersions}
                        sorting={sorting}
                        setSorting={setSorting}
                        columnFilters={columnFilters}
                        setColumnFilters={setColumnFilters}
                        isLoading={isLoadingVersions}
                        manualSorting={false}
                        manualFiltering={false}
                        noData={
                            <NoData>
                                No programs uploaded yet for this sub-program. Click the plus button on top right to
                                upload program files.
                            </NoData>
                        }
                        rowSelection={rowSelection}
                        setRowSelection={setRowSelection}
                    />
                </TableContainer>
            </WidgetWithTitle>
            <CopyVersionModal
                onHideModal={() => update({ showModal: false, versionHashCode: null, versionId: null })}
            />
        </>
    );
};

export default LibraryTable;
